import { useEffect, useState } from 'react';
import { DJANGO_API } from 'app-constants.ts';
import axios from 'axios';

type PublicSettingsResponse = {
    maintenance_mode: boolean;
    investor_registration: boolean;
    calendly_booking_url: string | null;
};

type PublicSettingsValues = {
    maintenance: boolean;
    investorRegistration: boolean;
    calendlyBookingUrl: string | null;
};

type PublicSettings = PublicSettingsValues & { loading: boolean };

const DEFAULT_SETTINGS: PublicSettingsValues = {
    maintenance: false,
    investorRegistration: true,
    calendlyBookingUrl: null,
};

let cachedSettings: PublicSettingsValues | null = null;
let pendingRequest: Promise<PublicSettingsValues> | null = null;

export function usePublicSettings(): PublicSettings {
    const [settings, setSettings] = useState(cachedSettings ?? DEFAULT_SETTINGS);
    const [loading, setLoading] = useState(cachedSettings === null);

    useEffect(() => {
        let cancelled = false;
        fetchPublicSettings()
            .then((fetched) => {
                if (!cancelled) setSettings(fetched);
            })
            .catch(() => {})
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    return { ...settings, loading };
}

function fetchPublicSettings(): Promise<PublicSettingsValues> {
    pendingRequest ??= axios
        .get<PublicSettingsResponse>(`${DJANGO_API}/public_settings/`)
        .then(({ data }) => {
            cachedSettings = {
                maintenance: data.maintenance_mode,
                investorRegistration: data.investor_registration,
                calendlyBookingUrl: data.calendly_booking_url,
            };
            return cachedSettings;
        })
        .catch((error: unknown) => {
            pendingRequest = null;
            throw error;
        });
    return pendingRequest;
}
